import type { ReactNode, SVGProps } from "react";

// HOME · Agent Finder · AI Assistant 안내 전용 아이콘 (Figma ver.7, 2026-10-14 수집 SVG 경로 그대로).
// 공용 nv-icons.tsx 는 건드리지 않고 이 영역 것만 따로 둬요. 모두 currentColor 를 따라가요(AI Assistant 상자 아이콘만 시안 색 고정).
type IconProps = Omit<SVGProps<SVGSVGElement>, "width" | "height"> & { size?: number };

// 로그인 유도 · 잠금 줄의 선 자물쇠 (Icon / Lock 14x14 · 16 로 키워 쓰는 자리: stroke #5E626A 1.33)
export function NvLockLineIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden {...props}>
      <path
        d="M11.0833 6.41602H2.91667C2.27233 6.41602 1.75 6.93835 1.75 7.58268V11.666C1.75 12.3103 2.27233 12.8327 2.91667 12.8327H11.0833C11.7277 12.8327 12.25 12.3103 12.25 11.666V7.58268C12.25 6.93835 11.7277 6.41602 11.0833 6.41602Z"
        stroke="currentColor"
        strokeWidth="1.16667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.08594 6.41602V4.08268C4.08594 3.30913 4.39323 2.56727 4.94021 2.02029C5.48719 1.47331 6.22906 1.16602 7.0026 1.16602C7.77615 1.16602 8.51802 1.47331 9.065 2.02029C9.61198 2.56727 9.91927 3.30913 9.91927 4.08268V6.41602"
        stroke="currentColor"
        strokeWidth="1.16667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// 결과 카드 대표 지표 띠의 추세 아이콘 (16x16, stroke 1.8)
export function NvTrendIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
      <path d="M14.6667 4.66667L9 10.3333L5.66667 7L1.33333 11.3333" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.6667 4.66667H14.6667V8.66667" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// 북마크 별 22x22 (Figma 847:49745 Bookmark): 기본 = 회색 외곽선(#A1A8B3), 북마크됨 = 주황 채움(#FD5108)
export function NvBookmarkStarIcon({ size = 22, filled = false, ...props }: IconProps & { filled?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 21 21" fill="none" aria-hidden {...props}>
      <path
        d="M10.5 1.75L13.2038 7.2275L19.25 8.11125L14.875 12.3725L15.9075 18.3925L10.5 15.5487L5.0925 18.3925L6.125 12.3725L1.75 8.11125L7.79625 7.2275L10.5 1.75Z"
        fill={filled ? "#FD5108" : "none"}
        stroke={filled ? "#FD5108" : "#A1A8B3"}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// 닫기 (Lucide x · 팝업 24px · 10/13 제안 06). 선 굵기 1.8
export function NvLucideXIcon({ size = 24, ...props }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <path d="M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 6L18 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// 더보기 · 접기 버튼의 화살표 (Icon / chevron (Lucide) 14x14, stroke 2)
export function NvLucideChevronIcon({ size = 14, direction = "down", ...props }: IconProps & { direction?: "down" | "up" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden style={{ transform: direction === "up" ? "rotate(180deg)" : undefined }} {...props}>
      <path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// AI Assistant 오픈 예정 안내의 기능 카드 아이콘 (Figma ver.7 "Icon / msg · chart · bot (외부 · 로그인 전용)": 44x44 r12 상자 + 선 아이콘)
function AssistantTile({ size, background, stroke, children, ...props }: IconProps & { background: string; stroke: string; children: ReactNode }) {
  return (
    <span aria-hidden className="inline-flex shrink-0 items-center justify-center rounded-[12px]" style={{ width: size, height: size, background }}>
      <svg width={size} height={size} viewBox="0 0 44 44" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        {children}
      </svg>
    </span>
  );
}

export function NvAssistantMsgTile({ size = 44, ...props }: IconProps) {
  return (
    <AssistantTile size={size} background="#FFF0E8" stroke="#FD5108" {...props}>
      <path d="M31 25C31 25.5304 30.7893 26.0391 30.4142 26.4142C30.0391 26.7893 29.5304 27 29 27H17L13 31V15C13 14.4696 13.2107 13.9609 13.5858 13.5858C13.9609 13.2107 14.4696 13 15 13H29C29.5304 13 30.0391 13.2107 30.4142 13.5858C30.7893 13.9609 31 14.4696 31 15V25Z" />
      <path d="M23 18H17" />
      <path d="M27 22H17" />
    </AssistantTile>
  );
}

export function NvAssistantChartTile({ size = 44, ...props }: IconProps) {
  return (
    <AssistantTile size={size} background="#FFF4E0" stroke="#EB8C00" {...props}>
      <path d="M13 13V31H31" />
      <path d="M28 27V19" />
      <path d="M23 27V15" />
      <path d="M18 27V24" />
    </AssistantTile>
  );
}

export function NvAssistantBotTile({ size = 44, ...props }: IconProps) {
  return (
    <AssistantTile size={size} background="#FCEBEF" stroke="#D93954" {...props}>
      <path d="M22 18V14H18" />
      <path d="M28 18H16C14.8954 18 14 18.8954 14 20V28C14 29.1046 14.8954 30 16 30H28C29.1046 30 30 29.1046 30 28V20C30 18.8954 29.1046 18 28 18Z" />
      <path d="M12 24H14" />
      <path d="M30 24H32" />
      <path d="M25 23V25" />
      <path d="M19 23V25" />
    </AssistantTile>
  );
}

// 칩 안 지우기 × (Icon / x (외부) 12 · 13px, stroke 1.6) — 선택한 조건 · AI 해석 칩
export function NvSmallXIcon({ size = 13, ...props }: IconProps) {
  const a = size * 0.25;
  const b = size * 0.75;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" aria-hidden {...props}>
      <path d={`M${b} ${a}L${a} ${b}`} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d={`M${a} ${a}L${b} ${b}`} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
